

import { computed } from 'vue';
import { defineStore } from 'pinia';

import { shopSdk } from '../graphql/shopClient';
import LoginDialog from './loginDialog.vue';
import { openDialog } from '../lib/promiseDialog';

import { useLazyAsyncState } from '../lib/useLazyAsyncState';

import { md5 } from 'js-md5';
import { Address, CreateAddressInput, UpdateAddressInput, UspsAddressInput } from '../graphql/generated-shop';
import { sortBy } from 'lodash';
import { handleSystemMessage } from '../lib/system-messages';

export interface UserRegistrationParams {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  callsign: string;
  phoneNumber?: string;
}

export function getEmptyAddress(): AddressInput {
  const addr: AddressInput = {
    fullName: '',
    company: '',
    streetLine1: '',
    streetLine2: '',
    city: '',
    province: '',
    postalCode: '',
    countryCode: 'US',
    phoneNumber: '',
    customFields: {
      firstName: '',
      lastName: '',
    },
  };
  return addr;
}

export function getBillingAddress(billingAddress: AddressInput) {
  const addr: AddressInput = {
    fullName: billingAddress?.fullName ?? '',
    company: billingAddress?.company ?? '',
    streetLine1: billingAddress?.streetLine1 ?? '',
    streetLine2: billingAddress?.streetLine2 ?? '',
    city: billingAddress?.city ?? '',
    province: billingAddress?.province ?? '',
    postalCode: billingAddress?.postalCode ?? '',
    countryCode: billingAddress?.countryCode ?? 'US',
    phoneNumber: billingAddress?.phoneNumber ?? '',
    customFields: {
      firstName: billingAddress?.customFields?.firstName ?? '',
      lastName: billingAddress?.customFields?.lastName ?? '',
    },
  };
  return addr;
}

const validEmailHistory: Record<string, boolean> = {};
export async function isValidEmail(email: string) {
  email = email.trim().toLowerCase();
  if (email in validEmailHistory) {
    return validEmailHistory[email];
  }
  const result = await shopSdk.IsValidEmail({ email: email.trim().toLowerCase() });
  const info = result.isValidEmail;
  if (info.__typename === 'ValidEmail') {
    validEmailHistory[email] = info.valid;
    return info.valid;
  } else {
    validEmailHistory[email] = false;
    throw new Error(`${info.__typename}: ${info.message}`);
  }
}

export async function validateUSPSAddress(addr: UspsAddressInput) {
  const result = await shopSdk.validateUspsAddress({input: addr});
  const info = result.validateUSPSAddress;
  if (info.__typename === 'USPSValidationError') {
    throw new Error(`${info.__typename}: ${info.message}`);
  } else {
    return info;
  }
}

// `id` is the only difference between CreateAddressInput and UpdateAddressInput,
// and this type is used for both creating and updating addresses
export type AddressInput = CreateAddressInput & { id?: string; }

function useActiveCustomer() {
  const {
    state: currentCustomer,
    execute: updateCurrentCustomer,
    isLoading: customerRefreshing,
    then: currentCustomerReadyPromise,
  } = useLazyAsyncState(async () => {
    const resp = await shopSdk.CurrentCustomer();
    if (typeof resp.systemMessage === 'string') {
      handleSystemMessage(resp.systemMessage);
    }
    const data = resp.activeCustomer;
    if (data?.__typename === 'Customer') {
      return data;
    } else {
      return null;
    }
  }, null, {
    immediate: true,
    resetOnExecute: false,
  });

  return {
    currentCustomer,
    updateCurrentCustomer,
    customerRefreshing,
    currentCustomerReadyPromise,
  };
}

function useActiveCustomerAddresses() {
  const {
    state: customerAddresses,
    execute: refreshCustomerAddresses,
    isLoading: customerAddressesRefreshing,
  } = useLazyAsyncState<Address[]>(async (): Promise<Address[]> => {
    const resp = await shopSdk.activeCustomerAddresses();

    const data = resp.activeCustomer;
    if (data?.id && data.addresses) {
      return sortBy(data.addresses as Address[], 'createdAt');
    } else {
      return [];
    }
  }, [], {
    immediate: false,
    resetOnExecute: false,
  });
  return {
    customerAddresses,
    refreshCustomerAddresses,
    customerAddressesRefreshing,
  };
}
function useActiveCustomerOrders() {
  const {
    state: customerOrders,
    execute: updateCustomerOrders,
    isLoading: customerOrdersRefreshing,
  } = useLazyAsyncState(async () => {
    const resp = await shopSdk.activeCustomerOrders({
      options: {
        filter: {
          state: { in: ['PaymentAuthorized', 'PaymentSettled', 'Shipped', 'Delivered', 'PartiallyShipped', 'OnHold'] },
        }
      }
    });

    const data = resp.activeCustomer;
    if (data?.id && data.orders) {
      return sortBy(data.orders.items, o => new Date(o.orderPlacedAt).getTime()).reverse();
    } else {
      return [];
    }
  }, null, {
    immediate: false,
    resetOnExecute: false,
  });
  return {
    customerOrders,
    updateCustomerOrders,
    customerOrdersRefreshing,
  };
}

export const useAccountStore = defineStore('account', () => {
  const lazyLoaded = {
    ...useActiveCustomer(),
    ...useActiveCustomerAddresses(),
    ...useActiveCustomerOrders(),
  };
  const {
    currentCustomer,
    updateCurrentCustomer
  } = lazyLoaded;

  const isLoggedIn = computed(() => {
    return !!currentCustomer.value;
  });

  const customerName = computed(() => {
    if (currentCustomer.value) {
      return currentCustomer.value.customFields?.callsign ||
        `${currentCustomer.value.firstName} ${currentCustomer.value.lastName}`;
    } else {
      return "Guest";
    }
  });

  async function logout() {
    await shopSdk.logout();
    // this does a page reload, so no need to update the customer / order info
    window.location.href = window.location.origin + '/shop';
  }
  async function showLogin() {
    await openDialog(LoginDialog);
  }

  async function tryLogin(email: string, password: string, rememberMe: boolean) {
    const result = await shopSdk.login({
      email, password, rememberMe: rememberMe,
    });

    const data = result.login;
    if (data.__typename === 'CurrentUser') {
      // Successful login
      await updateCurrentCustomer();
      return true;
    } else {
      return {
        type: data.__typename,
        message: data.message,
        code: data.errorCode,
      };
    }
  }

  async function resetPassword(token: string, password: string) {
    const result = await shopSdk.resetPassword({
      token, password,
    });

    const data = result.resetPassword;
    if (data.__typename === 'CurrentUser') {
      return true;
    } else {
      throw new Error(`${data.message}`);
    }
  }

  async function requestPasswordReset(emailAddress: string) {
    const result = await shopSdk.requestPasswordReset({
      emailAddress,
    });
    const data = result.requestPasswordReset;
    if (data?.__typename === 'Success') {
      return true;
    } else {
      const errorMessage = data?.message || "Unknown error. Please try again later.";
      throw new Error(errorMessage);
    }
  }

  async function updateUserInfo(params: { firstName: string, lastName: string, callsign: string }) {
    const result = await shopSdk.UpdateCustomer({
      input: {
        firstName: params.firstName,
        lastName: params.lastName,
        customFields: { callsign: params.callsign },
      }
    });
    if (result.updateCustomer?.__typename === "Customer") {
      await updateCurrentCustomer();
    }
    return result;
  }
  async function updateEmailAddress(newEmailAddress: string, password: string) {
    const result = await shopSdk.requestUpdateCustomerEmailAddress({ newEmailAddress, password});
    if (result.requestUpdateCustomerEmailAddress?.__typename !== "Success") {
      throw new Error(result.requestUpdateCustomerEmailAddress?.message || "Unknown error. Please try again later.");
    }
    return result;
  }
  async function changePassword(currentPassword: string, newPassword: string) {
    const result = await shopSdk.updateCustomerPasswordMutation({
      currentPassword,
      newPassword,
    });
    const data = result.updateCustomerPassword;
    switch(data.__typename) {
      case 'PasswordValidationError':
      case 'InvalidCredentialsError':
      case 'NativeAuthStrategyError':
        // throw new Error(`${data.__typename}: ${data.errorCode} - ${data.message}`);
        throw new Error(data.message);
      case 'Success':
        return result;
    }
  }

  async function createOrUpdateAddress(address: AddressInput) {
    try {
      address.fullName = `${address.customFields?.firstName} ${address.customFields?.lastName}`;
      if (!address.id) {
        const result = await shopSdk.createCustomerAddress({ input: address });
        if (!result.createCustomerAddress.id) {
          throw new Error("Unknown error. Please try again later.");
        }
      } else {
        const result = await shopSdk.updateCustomerAddress({ input: address as UpdateAddressInput });
        if (!result.updateCustomerAddress.id) {
          throw new Error("Unknown error. Please try again later.");
        }
      }
      await lazyLoaded.refreshCustomerAddresses();
    } catch {
      throw new Error("Unknown error. Please try again later.");
    }
  }
  async function deleteAddress(id: string) {
    const result = await shopSdk.deleteCustomerAddress({ id });
    if (!result.deleteCustomerAddress.success) {
      throw new Error("Unknown error. Please try again later.");
    }
    await lazyLoaded.refreshCustomerAddresses();
  }

  async function tryRegister(params: UserRegistrationParams) {
    try {
      const result = await shopSdk.registerCustomerAccount({
        input: {
          emailAddress: params.email,
          firstName: params.firstname,
          lastName: params.lastname,
          password: params.password,
          ...(params.callsign && { customFields: { callsign: params.callsign } }),
        }
      });

      const data = result.registerCustomerAccount;
      switch(data.__typename) {
        case 'MissingPasswordError':
        case 'NativeAuthStrategyError':
        case 'PasswordValidationError':
          throw new Error(`${data.__typename}: ${data.errorCode} - ${data.message}`);
        case 'Success':
          return data.success;
      }
    } catch (err) {
      console.error("Error registering:", err);
    }
  }

  const email = computed(() => {
    if (currentCustomer.value) {
      return currentCustomer.value.emailAddress;
    } else {
      return "";
    }
  });

  return {
    fullname: computed(() => {
      if (currentCustomer.value) {
        return `${currentCustomer.value.firstName} ${currentCustomer.value.lastName}`;
      } else {
        return "Guest";
      }
    }),
    email,
    avatarUrl: computed(() => {
      if (email.value) {
        return `https://www.gravatar.com/avatar/${md5(email.value)}?s=100`;
      } else {
        return "";
      }
    }),

    customerName,
    isLoggedIn,

    tryLogin,
    logout,
    showLogin,
    tryRegister,
    resetPassword,
    requestPasswordReset,
    updateUserInfo,
    updateEmailAddress,
    changePassword,

    createOrUpdateAddress,
    deleteAddress,

    // Lazy-loaded remote data
    ...lazyLoaded,
  };
});
